import { isModuleAllowedForRole } from '../../shared/rolePermissions';

export type WidgetSize = '1-col' | '2-col' | 'full';

export interface WidgetCatalogItem {
  id: string;
  code: string;
  title: string;
  category: 'METRIC' | 'TABLE' | 'FEED';
  requiredModule: string;
  defaultSize: WidgetSize;
  description: string;
}

export interface ConfiguredWidgetItem {
  id: string;
  widgetId: string; // references WidgetCatalogItem.id
  size: WidgetSize;
  order: number;
}

// Authoritative Catalog of All Available Dashboard Widgets
export const WIDGET_CATALOG: WidgetCatalogItem[] = [
  // 1. Metric Cards
  {
    id: 'kpi_my_reports',
    code: 'WGT-K-MY-REP',
    title: 'My Reports',
    category: 'METRIC',
    requiredModule: 'occurrences',
    defaultSize: '1-col',
    description: 'Personal assigned occurrence and hazard reports count with status breakdown'
  },
  {
    id: 'kpi_waiting_assessment',
    code: 'WGT-K-WAIT-ASSESS',
    title: 'Waiting Assessment',
    category: 'METRIC',
    requiredModule: 'ingestion',
    defaultSize: '1-col',
    description: 'New safety reports awaiting SMS triage and assessment by risk severity'
  },
  {
    id: 'kpi_feed_updates',
    code: 'WGT-K-FEED-UPD',
    title: 'New Feed Updates',
    category: 'METRIC',
    requiredModule: 'tasks_actions',
    defaultSize: '1-col',
    description: 'Work updates and collaborative action feed activity counter'
  },
  {
    id: 'kpi_overdue_actions',
    code: 'WGT-K-OVERDUE-ACT',
    title: 'Overdue Actions',
    category: 'METRIC',
    requiredModule: 'actions',
    defaultSize: '1-col',
    description: 'Safety mitigation actions and investigation tasks past target deadline'
  },
  {
    id: 'kpi_high_critical_risks',
    code: 'WGT-K-CRIT-RISKS',
    title: 'High & Critical Risks',
    category: 'METRIC',
    requiredModule: 'risks',
    defaultSize: '1-col',
    description: 'Operational risks in Red or Amber ALARP matrix bands requiring review'
  },
  {
    id: 'kpi_open_investigations',
    code: 'WGT-K-OPEN-INVS',
    title: 'Open Safety Investigations',
    category: 'METRIC',
    requiredModule: 'investigations',
    defaultSize: '1-col',
    description: 'Formal incident inquiries currently undergoing root-cause analysis'
  },
  {
    id: 'kpi_quality_findings',
    code: 'WGT-K-QMS-FINDINGS',
    title: 'Quality Non-Conformities',
    category: 'METRIC',
    requiredModule: 'findings',
    defaultSize: '1-col',
    description: 'Open Level 1 and Level 2 audit findings pending corrective action'
  },
  {
    id: 'kpi_compliance_score',
    code: 'WGT-K-COMPLIANCE',
    title: 'Compliance Monitoring Score',
    category: 'METRIC',
    requiredModule: 'monitoring',
    defaultSize: '1-col',
    description: 'ICAO / EASA regulatory compliance fulfillment score'
  },
  {
    id: 'kpi_moc_changes',
    code: 'WGT-K-MOC',
    title: 'Active MOC Changes',
    category: 'METRIC',
    requiredModule: 'moc',
    defaultSize: '1-col',
    description: 'Management of Change cases in risk assessment and approval phases'
  },
  {
    id: 'kpi_system_telemetry',
    code: 'WGT-K-SYSTEM-TELEMETRY',
    title: 'Integration Hub Telemetry',
    category: 'METRIC',
    requiredModule: 'integration_attempts',
    defaultSize: '1-col',
    description: 'External connector message flow, success rate and dead-letter queue'
  },
  {
    id: 'kpi_hazard_register',
    code: 'WGT-K-HAZARDS',
    title: 'Active Safety Hazards',
    category: 'METRIC',
    requiredModule: 'hazards',
    defaultSize: '1-col',
    description: 'Operational hazards currently monitored and mitigated in hazard register'
  },
  {
    id: 'kpi_open_capas',
    code: 'WGT-K-CAPAS',
    title: 'Active Corrective Actions (CAPA)',
    category: 'METRIC',
    requiredModule: 'capas',
    defaultSize: '1-col',
    description: 'Open CAPA cases undergoing root cause analysis and verification'
  },
  {
    id: 'kpi_spi_spt_performance',
    code: 'WGT-K-SPI-SPT',
    title: 'Safety Performance (SPI / SPT)',
    category: 'METRIC',
    requiredModule: 'performance',
    defaultSize: '1-col',
    description: 'Key safety performance targets, alert levels, and SPI compliance'
  },
  {
    id: 'kpi_missing_spi_data',
    code: 'WGT-K-SPI-MISSING',
    title: 'Missing SPI Data Entries',
    category: 'METRIC',
    requiredModule: 'performance',
    defaultSize: '1-col',
    description: 'Count of safety performance indicators pending data entry across your assigned scope'
  },
  {
    id: 'kpi_spi_threshold_alerts',
    code: 'WGT-K-SPI-ALERTS',
    title: 'Active SPI Alerts & Deviations',
    category: 'METRIC',
    requiredModule: 'performance',
    defaultSize: '1-col',
    description: 'Active SPI limit breaches and sigma alert conditions scoped to your operational role'
  },
  {
    id: 'kpi_upcoming_audits',
    code: 'WGT-K-UP-AUDITS',
    title: 'Upcoming Audits & Inspections',
    category: 'METRIC',
    requiredModule: 'audits',
    defaultSize: '1-col',
    description: 'Internal and external regulatory audit missions scheduled for execution'
  },
  {
    id: 'kpi_training_expirations',
    code: 'WGT-K-TRAIN-EXP',
    title: 'Expiring Training & Currency',
    category: 'METRIC',
    requiredModule: 'training',
    defaultSize: '1-col',
    description: 'Crew and maintenance qualifications, recurrent sessions and license expiries'
  },
  {
    id: 'kpi_asset_maintenance',
    code: 'WGT-K-ASSET-MAINT',
    title: 'Aircraft & Asset Maintenance',
    category: 'METRIC',
    requiredModule: 'assets',
    defaultSize: '1-col',
    description: 'Part-145 maintenance work orders and equipment calibration serviceability'
  },
  {
    id: 'kpi_pending_approvals',
    code: 'WGT-K-PEND-APPR',
    title: 'Pending Workflow Approvals',
    category: 'METRIC',
    requiredModule: 'workflows',
    defaultSize: '1-col',
    description: 'Formal management approvals, document publications, and MOC sign-offs'
  },
  {
    id: 'kpi_safety_surveys',
    code: 'WGT-K-SURVEYS',
    title: 'Safety Culture & Surveys',
    category: 'METRIC',
    requiredModule: 'surveys',
    defaultSize: '1-col',
    description: 'Safety climate surveys participation rate and open feedback campaigns'
  },
  {
    id: 'kpi_regulatory_obligations',
    code: 'WGT-K-REG-OBLIG',
    title: 'Exemptions & Authority Oversight',
    category: 'METRIC',
    requiredModule: 'obligations',
    defaultSize: '1-col',
    description: 'Active deviations, exemptions, and CAA/EASA regulatory notices'
  },
  {
    id: 'kpi_flight_occurrences',
    code: 'WGT-K-OCCURRENCES',
    title: 'Occurrences',
    category: 'METRIC',
    requiredModule: 'occurrences',
    defaultSize: '1-col',
    description: 'Mandatory and voluntary operational occurrences recorded across fleet'
  },

  // 2. Tables
  {
    id: 'table_my_reports',
    code: 'WGT-T-MY-REP',
    title: 'My Reports (Assigned Queue)',
    category: 'TABLE',
    requiredModule: 'occurrences',
    defaultSize: '2-col',
    description: 'Reports assigned to you with ID, title, status, and due date countdown'
  },
  {
    id: 'table_waiting_assessment',
    code: 'WGT-T-WAIT-ASSESS',
    title: 'Reports Waiting Assessment',
    category: 'TABLE',
    requiredModule: 'ingestion',
    defaultSize: '2-col',
    description: 'Incoming occurrences awaiting triage with kind, severity, and time received'
  },
  {
    id: 'table_missing_spi_data',
    code: 'WGT-T-SPI-MISSING',
    title: 'Missing SPI Data Entries (Action Required)',
    category: 'TABLE',
    requiredModule: 'performance',
    defaultSize: '2-col',
    description: 'Indicators requiring data input for elapsed months, quarters, and years by role and department'
  },
  {
    id: 'table_spi_threshold_alerts',
    code: 'WGT-T-SPI-ALERTS',
    title: 'Active SPI Threshold Breaches & Alerts',
    category: 'TABLE',
    requiredModule: 'performance',
    defaultSize: '2-col',
    description: 'Safety performance indicators in Alert or Breach conditions requiring immediate review'
  },
  {
    id: 'table_open_investigations',
    code: 'WGT-T-INVS',
    title: 'Active Investigations Register',
    category: 'TABLE',
    requiredModule: 'investigations',
    defaultSize: '2-col',
    description: 'In-flight investigation inquiries with investigator and phase'
  },
  {
    id: 'table_audit_schedule',
    code: 'WGT-T-AUDITS',
    title: 'Quality Audits & Inspections',
    category: 'TABLE',
    requiredModule: 'audits',
    defaultSize: '2-col',
    description: 'Upcoming annual audit programme missions, lead auditor, and date'
  },
  {
    id: 'table_open_capas',
    code: 'WGT-T-CAPAS',
    title: 'Corrective Actions (CAPA) Register',
    category: 'TABLE',
    requiredModule: 'capas',
    defaultSize: '2-col',
    description: 'Formal corrective and preventive action plans with target dates, owners, and verification'
  },
  {
    id: 'table_top_hazards',
    code: 'WGT-T-HAZARDS',
    title: 'Top Operational Hazards Register',
    category: 'TABLE',
    requiredModule: 'hazards',
    defaultSize: '2-col',
    description: 'Highest-risk operational hazards with risk indices, consequences, and mitigations'
  },
  {
    id: 'table_quality_findings',
    code: 'WGT-T-FINDINGS',
    title: 'Non-Conformities & Findings Register',
    category: 'TABLE',
    requiredModule: 'findings',
    defaultSize: '2-col',
    description: 'Level 1 and Level 2 audit findings pending root cause submission and closure'
  },
  {
    id: 'table_training_currency',
    code: 'WGT-T-TRAIN-CURR',
    title: 'Crew Qualifications & Currency Expirations',
    category: 'TABLE',
    requiredModule: 'training',
    defaultSize: '2-col',
    description: 'Upcoming expirations for CRM, recurrent flight simulator checks, and Part-66 licenses'
  },
  {
    id: 'table_pending_approvals',
    code: 'WGT-T-WORKFLOWS',
    title: 'Workflow Governance & Approval Requests',
    category: 'TABLE',
    requiredModule: 'workflows',
    defaultSize: '2-col',
    description: 'Pending multi-level sign-offs for high-consequence safety cases and publications'
  },
  {
    id: 'table_asset_maintenance',
    code: 'WGT-T-ASSETS',
    title: 'Fleet Assets & Calibration Register',
    category: 'TABLE',
    requiredModule: 'assets',
    defaultSize: '2-col',
    description: 'Aircraft line maintenance packages, tool calibration validity, and asset movements'
  },

  // 3. Activity Feeds
  {
    id: 'feed_report_activity',
    code: 'WGT-F-REPORT-ACT',
    title: 'Report Activity Feed',
    category: 'FEED',
    requiredModule: 'tasks_actions',
    defaultSize: '2-col',
    description: 'Recent collaborative audit and occurrence logs directly involving you'
  },
  {
    id: 'feed_system_activity',
    code: 'WGT-F-SYSTEM-ACT',
    title: 'System Activity Feed',
    category: 'FEED',
    requiredModule: 'dashboard',
    defaultSize: '2-col',
    description: 'Audit trail, system configuration and security change events'
  },
  {
    id: 'feed_audit_findings',
    code: 'WGT-F-FINDINGS-ACT',
    title: 'Quality & Audit Findings Feed',
    category: 'FEED',
    requiredModule: 'findings',
    defaultSize: '2-col',
    description: 'Live chronological stream of newly raised and verified non-conformities'
  }
];

// Factory Default Layouts by Role
export const FACTORY_ROLE_LAYOUTS: Record<string, ConfiguredWidgetItem[]> = {
  SAFETY_MANAGER: [
    { id: 'cfg-1', widgetId: 'kpi_my_reports', size: '1-col', order: 1 },
    { id: 'cfg-2', widgetId: 'kpi_waiting_assessment', size: '1-col', order: 2 },
    { id: 'cfg-3', widgetId: 'kpi_feed_updates', size: '1-col', order: 3 },
    { id: 'cfg-4', widgetId: 'kpi_overdue_actions', size: '1-col', order: 4 },
    { id: 'cfg-5', widgetId: 'table_my_reports', size: '2-col', order: 5 },
    { id: 'cfg-6', widgetId: 'table_waiting_assessment', size: '2-col', order: 6 },
    { id: 'cfg-spi-1', widgetId: 'table_missing_spi_data', size: '2-col', order: 7 },
    { id: 'cfg-spi-2', widgetId: 'table_spi_threshold_alerts', size: '2-col', order: 8 },
    { id: 'cfg-7', widgetId: 'feed_report_activity', size: '2-col', order: 9 },
    { id: 'cfg-8', widgetId: 'feed_system_activity', size: '2-col', order: 10 },
  ],
  QUALITY_MANAGER: [
    { id: 'cfg-q1', widgetId: 'kpi_quality_findings', size: '1-col', order: 1 },
    { id: 'cfg-q2', widgetId: 'kpi_overdue_actions', size: '1-col', order: 2 },
    { id: 'cfg-q3', widgetId: 'kpi_my_reports', size: '1-col', order: 3 },
    { id: 'cfg-q4', widgetId: 'kpi_feed_updates', size: '1-col', order: 4 },
    { id: 'cfg-q5', widgetId: 'table_audit_schedule', size: '2-col', order: 5 },
    { id: 'cfg-q6', widgetId: 'table_my_reports', size: '2-col', order: 6 },
    { id: 'cfg-q7', widgetId: 'feed_report_activity', size: '2-col', order: 7 },
  ],
  AUDITOR: [
    { id: 'cfg-a1', widgetId: 'kpi_quality_findings', size: '1-col', order: 1 },
    { id: 'cfg-a2', widgetId: 'kpi_overdue_actions', size: '1-col', order: 2 },
    { id: 'cfg-a3', widgetId: 'kpi_my_reports', size: '1-col', order: 3 },
    { id: 'cfg-a4', widgetId: 'table_audit_schedule', size: 'full', order: 4 },
    { id: 'cfg-a5', widgetId: 'table_my_reports', size: 'full', order: 5 },
  ],
  LINE_PILOT: [
    { id: 'cfg-p1', widgetId: 'kpi_my_reports', size: '1-col', order: 1 },
    { id: 'cfg-p2', widgetId: 'kpi_spi_threshold_alerts', size: '1-col', order: 2 },
    { id: 'cfg-p3', widgetId: 'kpi_missing_spi_data', size: '1-col', order: 3 },
    { id: 'cfg-p4', widgetId: 'kpi_feed_updates', size: '1-col', order: 4 },
    { id: 'cfg-p5', widgetId: 'table_spi_threshold_alerts', size: '2-col', order: 5 },
    { id: 'cfg-p6', widgetId: 'table_missing_spi_data', size: '2-col', order: 6 },
    { id: 'cfg-p7', widgetId: 'table_my_reports', size: 'full', order: 7 },
    { id: 'cfg-p8', widgetId: 'feed_report_activity', size: 'full', order: 8 },
  ],
  SYSTEM_ADMIN: [
    { id: 'cfg-s1', widgetId: 'kpi_my_reports', size: '1-col', order: 1 },
    { id: 'cfg-s2', widgetId: 'kpi_waiting_assessment', size: '1-col', order: 2 },
    { id: 'cfg-s3', widgetId: 'kpi_spi_threshold_alerts', size: '1-col', order: 3 },
    { id: 'cfg-s4', widgetId: 'kpi_system_telemetry', size: '1-col', order: 4 },
    { id: 'cfg-s5', widgetId: 'table_my_reports', size: '2-col', order: 5 },
    { id: 'cfg-s6', widgetId: 'table_waiting_assessment', size: '2-col', order: 6 },
    { id: 'cfg-s7', widgetId: 'table_spi_threshold_alerts', size: '2-col', order: 7 },
    { id: 'cfg-s8', widgetId: 'table_missing_spi_data', size: '2-col', order: 8 },
    { id: 'cfg-s9', widgetId: 'feed_report_activity', size: '2-col', order: 9 },
    { id: 'cfg-s10', widgetId: 'feed_system_activity', size: '2-col', order: 10 },
  ],
  ORGANIZATION_ADMIN: [
    { id: 'cfg-o1', widgetId: 'kpi_my_reports', size: '1-col', order: 1 },
    { id: 'cfg-o2', widgetId: 'kpi_waiting_assessment', size: '1-col', order: 2 },
    { id: 'cfg-o3', widgetId: 'kpi_spi_threshold_alerts', size: '1-col', order: 3 },
    { id: 'cfg-o4', widgetId: 'kpi_system_telemetry', size: '1-col', order: 4 },
    { id: 'cfg-o5', widgetId: 'table_my_reports', size: '2-col', order: 5 },
    { id: 'cfg-o6', widgetId: 'table_waiting_assessment', size: '2-col', order: 6 },
    { id: 'cfg-o7', widgetId: 'table_spi_threshold_alerts', size: '2-col', order: 7 },
    { id: 'cfg-o8', widgetId: 'table_missing_spi_data', size: '2-col', order: 8 },
    { id: 'cfg-o9', widgetId: 'feed_report_activity', size: '2-col', order: 9 },
    { id: 'cfg-o10', widgetId: 'feed_system_activity', size: '2-col', order: 10 },
  ],
};

// Filter widget catalog according to role permissions
export function getAllowedWidgetsForRole(role: string): WidgetCatalogItem[] {
  const normRole = (role || 'USER').toUpperCase();
  return WIDGET_CATALOG.filter((item) => isModuleAllowedForRole(normRole, item.requiredModule));
}

// Get the Admin-configured or Factory default layout for a role
export function getRoleDefaultLayout(role: string): ConfiguredWidgetItem[] {
  const normRole = (role || 'SAFETY_MANAGER').toUpperCase();
  try {
    const saved = localStorage.getItem(`dash_role_default_${normRole}`);
    if (saved) {
      const parsed: ConfiguredWidgetItem[] = JSON.parse(saved);
      // Validate that all items in the saved layout are still allowed for this role
      const allowed = getAllowedWidgetsForRole(normRole).map((w) => w.id);
      return parsed.filter((item) => allowed.includes(item.widgetId));
    }
  } catch {}

  const factory = FACTORY_ROLE_LAYOUTS[normRole] || FACTORY_ROLE_LAYOUTS['SAFETY_MANAGER'];
  const allowed = getAllowedWidgetsForRole(normRole).map((w) => w.id);
  return factory.filter((item) => allowed.includes(item.widgetId));
}

// Save Admin-configured default layout for a role
export function saveRoleDefaultLayout(role: string, layout: ConfiguredWidgetItem[]): void {
  const normRole = (role || 'SAFETY_MANAGER').toUpperCase();
  localStorage.setItem(`dash_role_default_${normRole}`, JSON.stringify(layout));
}

// Reset Role layout to factory defaults
export function resetRoleToFactoryDefault(role: string): ConfiguredWidgetItem[] {
  const normRole = (role || 'SAFETY_MANAGER').toUpperCase();
  localStorage.removeItem(`dash_role_default_${normRole}`);
  return getRoleDefaultLayout(normRole);
}

// Get effective dashboard layout for a user (personal customized layout OR role default)
export function getUserDashboardLayout(userId: string, role: string): ConfiguredWidgetItem[] {
  const normRole = (role || 'SAFETY_MANAGER').toUpperCase();
  const allowed = getAllowedWidgetsForRole(normRole).map((w) => w.id);

  try {
    const saved = localStorage.getItem(`dash_user_layout_${userId}`);
    if (saved) {
      const parsed: ConfiguredWidgetItem[] = JSON.parse(saved);
      // Security enforcement: User can ONLY see widgets allowed by their current role
      const sanitized = parsed.filter((item) => allowed.includes(item.widgetId));
      if (sanitized.length > 0) {
        return sanitized;
      }
    }
  } catch {}

  // Fallback to role default
  return getRoleDefaultLayout(normRole);
}

// Save user's personal customized layout
export function saveUserDashboardLayout(userId: string, layout: ConfiguredWidgetItem[]): void {
  localStorage.setItem(`dash_user_layout_${userId}`, JSON.stringify(layout));
}

// Clear user customization so it inherits the role default
export function resetUserDashboardLayout(userId: string): void {
  localStorage.removeItem(`dash_user_layout_${userId}`);
}
